/**
 * Iter-193f — Expense Notes (Notes de Frais).
 *
 * Mobile-first layout: a "+ Scanner un ticket" CTA at the top, then the
 * current month's note with its line table. Scanning uses the device camera
 * via <input capture="environment"> which opens the rear camera on phones
 * and falls back to a regular file picker on desktop.
 *
 * Flow:
 *   1. User taps "+ Scanner" → picks an image.
 *   2. POST /expenses/ocr returns a draft + Drive link.
 *   3. EditLineDialog opens prefilled; user validates → POST /expenses/lines.
 *   4. The table refreshes and shows the new line.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import CrossMonthSearchPanel from "../components/CrossMonthSearchPanel";
import {
  AlertTriangle, ArrowDown, ArrowLeftRight, ArrowRight, ArrowUp, Camera, Check, ChevronLeft, ChevronRight, Download, FileText, Loader2, Pencil, Plus, RefreshCw,
  FileDown, FileSpreadsheet, ScanLine, Send, Trash2, X,
} from "lucide-react";
import IconAction from "@/components/IconAction";
import { SwitchLineButton } from "@/components/SwitchLineButton";
import ScannedPdfPreview from "@/components/ScannedPdfPreview";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const CATEGORY_BG = {
  Restauration: "bg-orange-100 text-orange-800",
  Carburant: "bg-amber-100 text-amber-800",
  "Hôtel": "bg-purple-100 text-purple-800",
  Transport: "bg-sky-100 text-sky-800",
  Alimentation: "bg-emerald-100 text-emerald-800",
  "Électronique": "bg-blue-100 text-blue-800",
  Loisirs: "bg-pink-100 text-pink-800",
  Abonnements: "bg-indigo-100 text-indigo-800",
  Autre: "bg-zinc-100 text-zinc-700",
};

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 });

export default function Expenses() {
  const navigate = useNavigate();
  const [feature, setFeature] = useState(null);
  // Iter-193t — multi-NDF per month: `notes` is an array of {note, lines}.
  // The editable DRAFT is identified by `activeNoteId` and is the FIRST place
  // any new line goes.
  const [notes, setNotes] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const ocrAbortRef = useRef(null); // Iter-193j — AbortController for in-flight OCR upload
  const [editLine, setEditLine] = useState(null); // either a partial draft or an existing line
  // Iter-195y/195z — manual override on the duplicate flag. State MUST live
  // before the early returns (`if (!feature) return …`) so hook order stays
  // stable across renders.
  const [overrideTarget, setOverrideTarget] = useState(null); // {line, mode:"on"|"off"}
  // Iter-193g — month navigation (defaults to current month).
  const todayInit = new Date();
  const [viewYear, setViewYear] = useState(todayInit.getFullYear());
  const [viewMonth, setViewMonth] = useState(todayInit.getMonth() + 1);
  const fileInputRef = useRef(null);
  // Iter-193u — date sorting toggle for line tables. Default: descending.
  const [sortDir, setSortDir] = useState("desc");
  const [switchCap, setSwitchCap] = useState(null); // Iter-314 — CEO NDF ⇄ cabinet switch
  const [bulkBusy, setBulkBusy] = useState(false); // Iter-318
  const [gotoDraft, setGotoDraft] = useState("");

  const loadMonth = async (y, m) => {
    setLoading(true);
    api.get(`/expenses/switch/status?year=${y}&month=${m}`).then((r) => setSwitchCap(r.data)).catch(() => setSwitchCap(null));
    try {
      const r = await api.get(`/expenses/notes/by-month/${y}/${m}`);
      const data = r.data || {};
      // Iter-193t — accept BOTH shapes:
      //   new: { notes: [{note, lines}, ...], active_note_id }
      //   old: { note, lines }   (PROD backends not yet redeployed)
      let nextNotes = [];
      let nextActive = null;
      if (Array.isArray(data.notes) && data.notes.length > 0) {
        nextNotes = data.notes;
        nextActive = data.active_note_id || data.notes[0]?.note?.expense_note_id || null;
      } else if (data.note) {
        nextNotes = [{ note: data.note, lines: data.lines || [] }];
        nextActive = data.note.expense_note_id;
      }
      setNotes(nextNotes);
      setActiveNoteId(nextActive);
    } catch (e) {
      if (e?.response?.status === 403) {
        // Feature not unlocked.
      } else {
        toast.error(e?.response?.data?.detail || "Erreur de chargement");
      }
      // Iter-193t — make sure we exit the "loading" placeholder even on error.
      setNotes([]);
      setActiveNoteId(null);
    } finally {
      setLoading(false);
    }
  };
  const [searchReload, setSearchReload] = useState(0);
  const loadCurrent = () => { setSearchReload((k) => k + 1); return loadMonth(viewYear, viewMonth); };
  // Iter-317 — year typed manually (any year, not only the last 5).
  const [yearDraft, setYearDraft] = useState(String(viewYear));
  useEffect(() => { setYearDraft(String(viewYear)); }, [viewYear]);
  const commitYear = () => {
    const y = Number(yearDraft);
    if (Number.isInteger(y) && y >= 2000 && y <= new Date().getFullYear() + 1) setViewYear(y);
    else { toast.error(`Année invalide (2000 – ${new Date().getFullYear() + 1})`); setYearDraft(String(viewYear)); }
  };

  // Iter-193t — derived helpers for the active editable note.
  const activeBlock = notes.find((b) => b?.note?.expense_note_id === activeNoteId) || null;
  const activeNote = activeBlock?.note || null;
  const activeLines = activeBlock?.lines || [];

  const shiftMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 1) { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    setViewYear(y);
    setViewMonth(m);
  };
  const jumpToCurrent = () => {
    const t = new Date();
    setViewYear(t.getFullYear());
    setViewMonth(t.getMonth() + 1);
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/expenses/feature-status");
        setFeature(r.data);
        if (r.data.unlocked) await loadMonth(viewYear, viewMonth);
      } catch (e) {
        toast.error("Impossible de vérifier l'accès à la fonctionnalité");
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Iter-193g — refresh when the user navigates months
  useEffect(() => {
    if (feature?.unlocked) loadMonth(viewYear, viewMonth);
    // eslint-disable-next-line
  }, [viewYear, viewMonth]);

  const statusBadge = (status) => {
    const tone = {
      DRAFT: "bg-zinc-100 text-zinc-700",
      SUBMITTED: "bg-amber-100 text-amber-900",
      VALIDATED: "bg-blue-100 text-blue-900",
      PAID: "bg-emerald-100 text-emerald-900",
      REJECTED: "bg-red-100 text-red-800",
    }[status] || "bg-zinc-100 text-zinc-700";
    const label = {
      DRAFT: "Brouillon",
      SUBMITTED: "Soumise",
      VALIDATED: "Validée",
      PAID: "Réglée",
      REJECTED: "Rejetée",
    }[status] || status;
    return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono uppercase ${tone}`}>{label}</span>;
  };

  if (!feature) {
    return (
      <div className="px-6 py-12 flex items-center gap-2 text-zinc-500">
        <Loader2 size={16} className="animate-spin" /> Chargement…
      </div>
    );
  }
  if (!feature.unlocked) {
    return (
      <div className="px-6 py-16 max-w-xl mx-auto text-center" data-testid="expenses-locked">
        <ScanLine size={42} className="mx-auto text-zinc-400 mb-4" />
        <h1 className="font-heading text-xl font-bold tracking-tight">Notes de frais</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Cette fonctionnalité est en cours d'activation pour votre compte. Demandez à votre CEO de
          débloquer l'accès depuis les paramètres.
        </p>
        <button
          onClick={() => navigate("/dashboard")}
          className="mt-6 inline-flex items-center gap-1.5 px-3 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
        >
          Retour
        </button>
      </div>
    );
  }

  const onPickFile = () => fileInputRef.current?.click();

  const onFileSelected = async (e) => {
    const fileList = Array.from(e.target.files || []);
    e.target.value = "";
    if (fileList.length === 0) return;
    // Iter-193j — accept by extension fallback if the OS reports a generic
    // type (Google Drive File Stream picks often report
    // application/octet-stream). The backend repeats this check.
    const isAcceptable = (f) => {
      const t = (f.type || "").toLowerCase();
      if (feature.supported_mimes.includes(t)) return true;
      const n = (f.name || "").toLowerCase();
      return /\.(jpg|jpeg|png|webp|heic|heif|pdf)$/i.test(n);
    };
    const unsupported = fileList.filter((f) => !isAcceptable(f));
    if (unsupported.length) {
      toast.error(
        `Format non supporté : ${unsupported.map((f) => f.name).join(", ")}. ` +
        (feature.accepted_file_types_label || "JPG/PNG/WEBP/HEIC/PDF, max 10 MB.") +
        " Si le fichier est dans Google Drive, téléchargez-le d'abord en local."
      );
      return;
    }
    // Iter-194a — bulk scan restored (up to 10 receipts per batch).
    if (fileList.length > 10) {
      toast.error("Maximum 10 tickets à la fois. Réessayez avec un lot plus petit.");
      return;
    }
    await runOcrOnReadyFiles(fileList);
  };

  // Iter-194a — bulk OCR restored. Single file → /expenses/ocr. Multi → /expenses/ocr/bulk
  // (parallel server-side). After OCR, the first ticket opens in the edit dialog; remaining
  // ones are queued via `_queue` and surface one by one after each save.
  const runOcrOnReadyFiles = async (files) => {
    if (!files || files.length === 0) return;
    const controller = new AbortController();
    ocrAbortRef.current = controller;
    setScanning(true);
    try {
      if (files.length === 1) {
        const fd = new FormData();
        fd.append("file", files[0]);
        const r = await api.post("/expenses/ocr", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          signal: controller.signal,
        });
        const d = r.data.draft || {};
        const ocrError = r.data.ocr_error;
        if (ocrError) {
          toast.warning(`OCR indisponible : ${ocrError}. Saisissez les informations à la main, le ticket reste joint.`);
        }
        setEditLine({
          invoice_date: d.invoice_date || new Date().toISOString().slice(0, 10),
          vendor: d.vendor || "", invoice_number: d.invoice_number || "",
          category: d.category || "Autre", amount: d.amount || "",
          currency: d.currency || "MAD", description: d.description || "",
          // Iter-207bb — pass through VAT fields from OCR (MAD invoices).
          amount_ht: d.amount_ht ?? "",
          vat_amount: d.vat_amount ?? "",
          vat_rate: d.vat_rate ?? "",
          amount_ttc: d.amount_ttc ?? (d.amount ?? ""),
          confidence: ocrError ? null : d.confidence,
          staged_file_id: r.data.staged_file?.staged_file_id,
          staged_mime: r.data.staged_file?.mime_type,
          auto_cropped: r.data.staged_file?.auto_cropped,
          _isNew: true,
          _ocrError: ocrError || null,
          _queue: r.data.extra_items || [],
          _queueTotal: 1 + (r.data.extra_items || []).length,
          _queueIndex: 1,
        });
        if ((r.data.extra_items || []).length) {
          toast.success(`${1 + r.data.extra_items.length} factures détectées dans ce fichier — elles vont s'enchaîner.`);
        }
      } else {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        const r = await api.post("/expenses/ocr/bulk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          signal: controller.signal,
        });
        const allItems = r.data.items || [];
        const items = allItems.filter((it) => !it.error && it.draft);
        const errors = allItems.filter((it) => it.error);
        errors.forEach((it) => toast.error(`${it.filename} : ${it.error}`));
        if (items.length === 0) {
          toast.error("Aucun ticket n'a pu être analysé.");
          return;
        }
        if (items.length < allItems.length) {
          toast.warning(`${items.length}/${allItems.length} tickets analysés. ${errors.length} échec(s).`);
        } else {
          toast.success(`${items.length} tickets analysés en parallèle.`);
        }
        const first = items[0];
        const queue = items.slice(1);
        const d = first.draft || {};
        setEditLine({
          invoice_date: d.invoice_date || new Date().toISOString().slice(0, 10),
          vendor: d.vendor || "", invoice_number: d.invoice_number || "",
          category: d.category || "Autre", amount: d.amount || "",
          currency: d.currency || "MAD", description: d.description || "",
          // Iter-207bb — pass through VAT fields from OCR (MAD invoices).
          amount_ht: d.amount_ht ?? "",
          vat_amount: d.vat_amount ?? "",
          vat_rate: d.vat_rate ?? "",
          amount_ttc: d.amount_ttc ?? (d.amount ?? ""),
          confidence: d.confidence,
          staged_file_id: first.staged_file?.staged_file_id,
          staged_mime: first.staged_file?.mime_type,
          _isNew: true,
          _queue: queue,
          _queueTotal: items.length,
          _queueIndex: 1,
        });
      }
    } catch (err) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || err?.message === "canceled") {
        toast.info("Analyse OCR annulée");
      } else {
        toast.error(err?.response?.data?.detail || "Échec OCR");
      }
    } finally {
      setScanning(false);
      ocrAbortRef.current = null;
    }
  };

  // Iter-193j — Cancel an in-flight OCR analysis.
  const cancelOcr = () => {
    if (ocrAbortRef.current) {
      ocrAbortRef.current.abort();
    }
  };

  const addManual = () => setEditLine({
    invoice_date: new Date().toISOString().slice(0, 10),
    vendor: "", invoice_number: "", category: "Autre",
    amount: "", currency: "MAD", description: "", _isNew: true, _queue: [],
  });

  const saveLine = async (payload, lineId, currentForm) => {
    try {
      let r;
      if (lineId) {
        r = await api.patch(`/expenses/lines/${lineId}`, payload);
        toast.success("Ligne mise à jour");
      } else {
        r = await api.post("/expenses/lines", payload);
        toast.success("Ligne ajoutée");
      }
      // Iter-292 — surface a Drive upload failure instead of silently keeping a staged copy.
      if (r?.data?.drive_warning) toast.warning(r.data.drive_warning, { duration: 9000 });
      // Iter-194a — bulk scan: if more OCR'd tickets remain in the queue,
      // pop the next one into the dialog so the user can review/save it
      // without re-opening the file picker.
      const queue = currentForm?._queue || [];
      if (queue.length > 0) {
        const next = queue[0];
        const rest = queue.slice(1);
        const d = next.draft || {};
        const total = currentForm._queueTotal || (queue.length + (currentForm._queueIndex || 1));
        const newIndex = (currentForm._queueIndex || 1) + 1;
        await loadCurrent();
        setEditLine({
          invoice_date: d.invoice_date || new Date().toISOString().slice(0, 10),
          vendor: d.vendor || "", invoice_number: d.invoice_number || "",
          category: d.category || "Autre", amount: d.amount || "",
          currency: d.currency || "MAD", description: d.description || "",
          // Iter-207bb — VAT fields (MAD).
          amount_ht: d.amount_ht ?? "",
          vat_amount: d.vat_amount ?? "",
          vat_rate: d.vat_rate ?? "",
          amount_ttc: d.amount_ttc ?? (d.amount ?? ""),
          confidence: d.confidence,
          staged_file_id: next.staged_file?.staged_file_id,
          staged_mime: next.staged_file?.mime_type,
          _isNew: true,
          _queue: rest,
          _queueTotal: total,
          _queueIndex: newIndex,
        });
        return;
      }
      setEditLine(null);
      await loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  const deleteLine = async (lineId) => {
    if (!confirm("Supprimer cette ligne ?")) return;
    try {
      await api.delete(`/expenses/lines/${lineId}`);
      await loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  // Iter-195y/195z — manual override on the duplicate flag. Used when the
  // user confirms the two flagged lines are actually distinct invoices (e.g.
  // two separate meals at the same restaurant on the same day). A short
  // reason can be attached and is shown to the validator.
  // NB: the corresponding `useState` lives near the other top-level hooks
  // above, to keep the hook order stable when `feature` is still loading.

  const openOverrideDialog = (line) => {
    setOverrideTarget({ line, mode: line.not_duplicate_override ? "off" : "on" });
  };

  const clearOverride = async (line) => {
    try {
      await api.patch(`/expenses/lines/${line.expense_line_id}/not-duplicate`, { not_duplicate: false });
      toast.success("Override annulé");
      await loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  const submitOverride = async (reason) => {
    if (!overrideTarget) return;
    try {
      await api.patch(`/expenses/lines/${overrideTarget.line.expense_line_id}/not-duplicate`, {
        not_duplicate: true,
        reason: (reason || "").trim() || null,
      });
      toast.success("Ligne marquée « pas un doublon »");
      setOverrideTarget(null);
      await loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  const toggleNotDuplicate = (line) => {
    if (line.not_duplicate_override) {
      // Already overridden — single confirm step to clear.
      if (!confirm("Annuler l'override « pas un doublon » sur cette ligne ?")) return;
      clearOverride(line);
    } else {
      // Open the reason dialog.
      openOverrideDialog(line);
    }
  };

  const submitNote = async (noteToSubmit) => {
    const target = noteToSubmit || activeNote;
    if (!target) return;
    if (!confirm(`Soumettre la NDF de ${target.month}/${target.year} pour remboursement ? Aucune modification ne sera plus possible.`)) return;
    try {
      await api.patch(`/expenses/notes/${target.expense_note_id}/submit`);
      toast.success("NDF soumise pour validation");
      await loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  // Iter-302 — a REJECTED NDF is editable in place (editing a line reopens it);
  // « Re-soumettre » re-sends it for validation in one click.
  const resubmitNote = async (noteToResubmit) => {
    if (!noteToResubmit) return;
    if (!confirm(`Re-soumettre cette NDF pour validation ?`)) return;
    try {
      if (noteToResubmit.status === "REJECTED") {
        await api.patch(`/expenses/notes/${noteToResubmit.expense_note_id}/resubmit`);
      }
      await api.patch(`/expenses/notes/${noteToResubmit.expense_note_id}/submit`);
      toast.success("NDF re-soumise pour validation");
      await loadCurrent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec");
    }
  };

  const download = async (kind, noteToExport) => {
    const target = noteToExport || activeNote;
    if (!target) return;
    try {
      const r = await api.get(`/expenses/notes/${target.expense_note_id}/export.${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      // Iter-194c — prefer the server-provided filename (significant format
      // `NDF_{user}_{year}-{month}_{status}_{ts}.{ext}`) when present.
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
      a.download = m ? decodeURIComponent(m[1]) : `NDF-${target.year}-${String(target.month).padStart(2, "0")}-${target.expense_note_id.slice(-4)}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec export");
    }
  };

  // Iter-318 — bulk delete / switch from the cross-month search.
  const runBulk = async (rows, clear, action) => {
    const label = action === "delete" ? "Supprimer" : "Basculer vers les dépenses internes";
    if (!window.confirm(`${label} ${rows.length} ligne(s) ?`)) return;
    setBulkBusy(true);
    let ok = 0;
    const errors = [];
    for (const l of rows) {
      try {
        if (action === "delete") await api.delete(`/expenses/lines/${l.expense_line_id}`);
        else await api.post(`/expenses/lines/${l.expense_line_id}/to-cabinet`);
        ok += 1;
      } catch (e) {
        errors.push(`${l.invoice_date} ${l.vendor || ""} : ${e?.response?.data?.detail || "échec"}`);
      }
    }
    setBulkBusy(false);
    if (ok) toast.success(`${ok} ligne(s) ${action === "delete" ? "supprimée(s)" : "basculée(s) vers les dépenses internes"}`);
    if (errors.length) toast.error(`${errors.length} échec(s) :\n${errors.slice(0, 5).join("\n")}`, { duration: 12000 });
    clear();
    await loadCurrent();
  };
  const searchBulkActions = (rows, clear) => (
    <>
      {switchCap?.allowed && (
        <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={bulkBusy}
          onClick={() => runBulk(rows, clear, "switch")} data-testid="ndf-search-bulk-switch">
          {bulkBusy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <ArrowLeftRight size={12} className="mr-1" />}
          Basculer ({rows.length})
        </Button>
      )}
      <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-700 border-red-200 hover:bg-red-50" disabled={bulkBusy}
        onClick={() => runBulk(rows, clear, "delete")} data-testid="ndf-search-bulk-delete">
        {bulkBusy ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Trash2 size={12} className="mr-1" />}
        Supprimer ({rows.length})
      </Button>
      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={clear} data-testid="ndf-search-bulk-clear">Désélectionner</Button>
    </>
  );

  // Iter-318 — « 03/2019 » → jump to that month.
  const gotoDate = () => {
    const t = gotoDraft.trim();
    const mt = t.match(/^(\d{1,2})\s*[/.-]\s*(\d{4})$/) || t.match(/^(\d{4})\s*[/.-]\s*(\d{1,2})$/);
    if (!mt) { toast.error("Format attendu : MM/AAAA (ex. 03/2019)"); return; }
    const [mo, yr] = mt[1].length === 4 ? [Number(mt[2]), Number(mt[1])] : [Number(mt[1]), Number(mt[2])];
    if (mo < 1 || mo > 12 || yr < 2000 || yr > new Date().getFullYear() + 1) { toast.error("Mois ou année invalide"); return; }
    setViewYear(yr);
    setViewMonth(mo);
    setGotoDraft("");
  };

  // Iter-317 — the cross-month search keeps every line action (receipt, edit, delete, switch, open month).
  const searchRowActions = (l) => {
    const st = l.note_status;
    const editableRow = st === "DRAFT" || st === "REJECTED";
    const [y, m] = String(l.invoice_date || "").split("-").map(Number);
    const lockLabel = st === "PAID" ? "payée" : st === "SUBMITTED" ? "en cours de validation" : "validée";
    return (
      <div className="flex flex-wrap items-center justify-end gap-1">
        {l.receipt_drive_web_view_link && (
          <a href={l.receipt_drive_web_view_link} target="_blank" rel="noreferrer" title="Voir le ticket"
            className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded" data-testid={`ndf-search-view-${l.expense_line_id}`}>
            <FileText size={13} />
          </a>
        )}
        {!l.receipt_drive_web_view_link && l.staged_file_id && !l.receipt_drive_file_id && (
          <a href={`/api/expenses/ocr/staged/${l.staged_file_id}/preview`} target="_blank" rel="noreferrer" title="Ticket en attente d'upload Drive"
            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded" data-testid={`ndf-search-pending-${l.expense_line_id}`}>
            <FileText size={13} />
          </a>
        )}
        {switchCap?.allowed && (
          <SwitchLineButton size="sm" direction="to-cabinet" lineId={l.expense_line_id}
            disabledReason={!editableRow ? `Bascule indisponible : NDF ${lockLabel}` : (l.billable_to_client ? "Ligne refacturable au client — retirez la refacturation avant de la basculer" : null)}
            onDone={loadCurrent} />
        )}
        <IconAction size="sm" icon={Pencil} testid={`ndf-search-edit-${l.expense_line_id}`} disabled={!editableRow}
          label={editableRow ? "Modifier la ligne" : `Modification impossible : NDF ${lockLabel}`}
          onClick={() => setEditLine({ ...l, _isNew: false })} />
        <IconAction size="sm" icon={Trash2} tone="danger" testid={`ndf-search-delete-${l.expense_line_id}`} disabled={!editableRow}
          label={editableRow ? "Supprimer la ligne" : `Suppression impossible : NDF ${lockLabel}`}
          onClick={() => deleteLine(l.expense_line_id)} />
        {y && m && (
          <IconAction size="sm" icon={ChevronRight} testid={`ndf-search-goto-${l.expense_line_id}`} label={`Ouvrir la NDF de ${String(m).padStart(2, "0")}/${y}`}
            onClick={() => { setViewYear(y); setViewMonth(m); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        )}
      </div>
    );
  };

  const isCurrentMonthView = (() => {
    const t = new Date();
    return viewYear === t.getFullYear() && viewMonth === (t.getMonth() + 1);
  })();

  const editable = activeNote?.status === "DRAFT";

  return (
    <div className="px-4 sm:px-6 py-6 max-w-5xl mx-auto" data-testid="expenses-page">
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Notes de frais</h1>
          {/* Iter-193g — month navigator */}
          <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid="expenses-month-nav">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              data-testid="expenses-month-prev"
              className="inline-flex items-center justify-center w-7 h-7 border border-zinc-200 rounded hover:bg-zinc-50"
              title="Mois précédent"
            >
              <ChevronLeft size={14} />
            </button>
            <select
              value={viewMonth}
              onChange={(e) => setViewMonth(Number(e.target.value))}
              data-testid="expenses-month-select"
              className="h-7 px-2 text-sm font-mono font-semibold bg-zinc-100 border border-zinc-200 rounded cursor-pointer hover:bg-zinc-200/60"
            >
              {["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"].map((l, idx) => (
                <option key={idx + 1} value={idx + 1}>{l}</option>
              ))}
            </select>
            <input
              type="number" inputMode="numeric" min={2000} max={new Date().getFullYear() + 1}
              value={yearDraft}
              onChange={(e) => setYearDraft(e.target.value)}
              onBlur={commitYear}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              data-testid="expenses-year-select"
              title="Saisissez une année puis Entrée"
              className="h-7 w-[4.5rem] px-2 text-sm font-mono font-semibold bg-zinc-100 border border-zinc-200 rounded hover:bg-zinc-200/60 focus:bg-white focus:outline-none focus:ring-1 focus:ring-zinc-400"
            />
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              data-testid="expenses-month-next"
              className="inline-flex items-center justify-center w-7 h-7 border border-zinc-200 rounded hover:bg-zinc-50"
              title="Mois suivant"
            >
              <ChevronRight size={14} />
            </button>
            <input
              type="text" inputMode="numeric" placeholder="MM/AAAA" value={gotoDraft}
              onChange={(e) => setGotoDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") gotoDate(); }}
              title="Aller directement à un mois : tapez par ex. 03/2019 puis Entrée"
              data-testid="expenses-goto-input"
              className="h-7 w-[5.5rem] ml-1 px-2 text-xs font-mono border border-zinc-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-zinc-400"
            />
            <IconAction icon={ArrowRight} size="sm" testid="expenses-goto-btn" label="Aller à ce mois" disabled={!gotoDraft.trim()} onClick={gotoDate} />
            {!isCurrentMonthView && (
              <button
                type="button"
                onClick={jumpToCurrent}
                data-testid="expenses-month-today"
                className="ml-1 text-[10px] font-mono uppercase text-zinc-600 hover:text-zinc-900 px-2 h-7 rounded hover:bg-zinc-50"
              >
                Aujourd'hui
              </button>
            )}
          </div>
          {/* Iter-193t — show a summary of ALL notes for the month */}
          {notes.length > 0 && (
            <div className="text-xs text-zinc-500 mt-2 flex items-center gap-2 flex-wrap" data-testid="expenses-month-summary">
              <span><strong className="text-zinc-700">{notes.length}</strong> note(s)</span>
              <span>·</span>
              <span><strong className="text-zinc-700">{notes.reduce((s, b) => s + (b.note?.n_lines || 0), 0)}</strong> ligne(s)</span>
              <span>·</span>
              <span>Total mois <strong className="text-zinc-900 font-mono">{fmt(notes.reduce((s, b) => s + (b.note?.total_mad || 0), 0))} MAD</strong></span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <IconAction icon={RefreshCw} size="lg" testid="expenses-refresh" label="Actualiser" loading={loading} onClick={loadCurrent} />
        </div>
      </div>

      {/* Iter-228x-bis — Cross-month search over personal NDF lines. */}
      <div className="mb-4">
        <CrossMonthSearchPanel
          kind="mine"
          testidPrefix="ndf-mine-search"
          csvFilenamePrefix="ndf_perso"
          categories={feature?.categories || []}
          reloadKey={searchReload}
          renderActions={searchRowActions}
          bulkActions={searchBulkActions}
          isRowSelectable={(l) => l.note_status === "DRAFT" || l.note_status === "REJECTED"}
        />
      </div>

      {/* Capture CTA */}
      {editable && (
        <div className="mb-6 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={onPickFile}
              disabled={scanning}
              data-testid="expenses-scan-btn"
              className="flex items-center justify-center gap-2 h-24 sm:h-20 rounded-lg border-2 border-dashed border-zinc-300 hover:border-zinc-950 hover:bg-zinc-50 text-zinc-700 font-medium disabled:opacity-50"
            >
              {scanning ? (
                <><Loader2 size={18} className="animate-spin" /> Analyse en cours…</>
              ) : (
                <><Camera size={20} /> Scanner un ticket <span className="text-[10px] font-mono text-zinc-500">(jusqu'à 10)</span></>
              )}
            </button>
            <button
              type="button"
              onClick={addManual}
              data-testid="expenses-add-manual-btn"
              className="flex items-center justify-center gap-2 h-24 sm:h-20 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700 font-medium"
            >
              <Plus size={18} /> Saisie manuelle
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,application/pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf"
              capture="environment"
              multiple
              onChange={onFileSelected}
              className="hidden"
              data-testid="expenses-file-input"
            />
          </div>
          {/* Iter-193j — accepted file types hint + cancel-during-scan */}
          <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-zinc-500" data-testid="expenses-upload-hint">
            <div>
              <span className="font-mono uppercase tracking-wider mr-2">Formats :</span>
              {feature.accepted_file_types_label || "JPG, PNG, WEBP, HEIC, PDF — max 10 MB"}
            </div>
            {scanning && (
              <button
                type="button"
                onClick={cancelOcr}
                data-testid="expenses-cancel-ocr"
                className="inline-flex items-center gap-1 px-2 h-7 border border-red-200 text-red-700 text-[11px] rounded hover:bg-red-50"
              >
                Annuler l'analyse
              </button>
            )}
          </div>
        </div>
      )}

      {/* Lines tables — Iter-193t: one block per NDF for the selected month.
          Frozen NDFs (SUBMITTED/VALIDATED/PAID/REJECTED) are read-only and
          stacked below the editable DRAFT. */}
      {notes.length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-zinc-500 text-sm" data-testid="expenses-empty-state">
          {loading ? (
            <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</span>
          ) : (
            <>
              Aucune note de frais ce mois-ci.
              <div className="mt-2 text-[11px] text-zinc-400">Scannez un ticket ou ajoutez une ligne manuellement pour créer une NDF.</div>
            </>
          )}
        </div>
      )}
      <div className="space-y-6">
        {notes.map((block, idx) => {
          const n = block.note;
          const ls = block.lines || [];
          const isActive = n.expense_note_id === activeNoteId;
          // Iter-194k — bug fix: a NDF whose validation was REVOKED comes back
          // to status=DRAFT. If a fresh empty DRAFT had been auto-created in
          // the same month (multi-NDF flow), the revoked NDF lost its
          // "active" tag and therefore became read-only with no submit
          // button. We now treat every DRAFT row as editable / submittable
          // independently — the line-edit / delete endpoints take a
          // `line_id` so there is no ambiguity, and `submitNote` accepts any
          // target note. Note: the "+ Scanner / Add manually" CTA still
          // targets the *active* DRAFT (latest one) since the OCR / lazy-
          // create logic is scoped there.
          const isRevoked = !!n.revoked_at;
          const rowEditable = n.status === "DRAFT" || n.status === "REJECTED";
          const dupCount = ls.filter((l) => l.is_duplicate).length;
          return (
            <section
              key={n.expense_note_id}
              data-testid={`expenses-note-block-${n.expense_note_id}`}
              data-note-status={n.status}
              className={`bg-white border rounded-lg overflow-hidden ${isActive ? "border-zinc-300" : "border-zinc-200"}`}
            >
              <header className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/60 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                    NDF #{idx + 1}
                  </span>
                  <span className="font-mono text-[10px] text-zinc-400">{n.expense_note_id.slice(-6)}</span>
                  {/* Iter-207t — alphanumeric règlement reference, assigned at first submission. */}
                  {n.reference && (
                    <span
                      data-testid={`expenses-note-reference-${n.expense_note_id}`}
                      title="Référence de règlement (visible sur le PDF et dans les emails)"
                      className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono font-bold uppercase tracking-wider bg-[#1F4FA3]/10 text-[#1F4FA3] border border-[#1F4FA3]/25"
                    >
                      {n.reference}
                    </span>
                  )}
                  {statusBadge(n.status)}
                  {isRevoked && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-mono uppercase bg-amber-100 text-amber-900"
                      data-testid={`expenses-note-revoked-tag-${n.expense_note_id}`}
                      title={n.revoke_reason || "Validation annulée"}
                    >
                      Réouverte
                    </span>
                  )}
                  {isActive && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono uppercase bg-zinc-950 text-white" data-testid={`expenses-note-active-tag-${n.expense_note_id}`}>
                      Active
                    </span>
                  )}
                  <span className="text-xs text-zinc-500">
                    {n.n_lines} ligne(s) · <strong className="text-zinc-900 font-mono">{fmt(n.total_mad)} MAD</strong>
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <IconAction icon={FileDown} testid={`expenses-export-pdf-${n.expense_note_id}`} label="Exporter la note de frais en PDF" onClick={() => download("pdf", n)} />
                  <IconAction icon={FileText} testid={`expenses-export-csv-${n.expense_note_id}`} label="Exporter en CSV" onClick={() => download("csv", n)} />
                  <IconAction icon={FileSpreadsheet} tone="emeraldSoft" testid={`expenses-export-xlsx-${n.expense_note_id}`} label="Exporter en Excel" onClick={() => download("xlsx", n)} />
                </div>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`expenses-table-${n.expense_note_id}`}>
                  <thead className="bg-zinc-50">
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
                      <th className="px-2 py-2">
                        <button
                          type="button"
                          onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                          data-testid={`expenses-sort-date-${n.expense_note_id}`}
                          className="inline-flex items-center gap-1 hover:text-zinc-900"
                          title={`Trier par date (actuel: ${sortDir === "asc" ? "croissant" : "décroissant"})`}
                        >
                          Date
                          {sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                        </button>
                      </th>
                      <th className="px-2 py-2">Fournisseur</th>
                      <th className="px-2 py-2 hidden lg:table-cell">N° Fact.</th>
                      <th className="px-2 py-2">Catégorie</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Montant</th>
                      <th className="px-2 py-2 text-right whitespace-nowrap">Conv. MAD</th>
                      <th className="px-2 py-2">Fact.</th>
                      <th className="px-2 py-2">Client</th>
                      <th className="px-2 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ls.length === 0 && (
                      <tr><td colSpan={9} className="px-2 py-8 text-center text-zinc-500 text-sm">
                        {isActive ? "Aucune dépense saisie. Scannez un ticket ou ajoutez une ligne manuellement." : "Aucune ligne."}
                      </td></tr>
                    )}
                    {ls.map((l) => (
                      <tr
                        key={l.expense_line_id}
                        className={`border-t border-zinc-100 hover:bg-zinc-50/60 ${l.is_duplicate ? "bg-red-50/60 hover:bg-red-50" : (l.overridden_duplicate ? "bg-amber-50/40 hover:bg-amber-50" : "")}`}
                        data-testid={`expenses-row-${l.expense_line_id}`}
                        title={l.is_duplicate
                          ? "Doublon détecté (même date, fournisseur, montant, n° de facture). Modifiez, supprimez, ou marquez comme \"Pas un doublon\" pour débloquer la soumission."
                          : (l.overridden_duplicate ? "Vous avez confirmé que cette ligne n'est pas un doublon — visible par le valideur." : undefined)}
                      >
                        <td className="px-2 py-3 font-mono text-xs whitespace-nowrap">
                          {l.is_duplicate && (
                            <AlertTriangle size={12} className="inline mr-1 text-red-700" data-testid={`expenses-row-dup-icon-${l.expense_line_id}`} />
                          )}
                          {l.overridden_duplicate && !l.is_duplicate && (
                            <Check size={12} className="inline mr-1 text-amber-700" data-testid={`expenses-row-dup-override-icon-${l.expense_line_id}`} />
                          )}
                          {l.invoice_date}
                        </td>
                        <td className="px-2 py-3 text-sm max-w-[180px]">
                          <div className="font-medium truncate" title={l.vendor}>{l.vendor}</div>
                          {l.description && <div className="text-[10px] text-zinc-500 truncate" title={l.description}>{l.description}</div>}
                        </td>
                        <td className="px-2 py-3 font-mono text-xs text-zinc-600 hidden lg:table-cell max-w-[120px] break-all whitespace-normal align-top">{l.invoice_number || "—"}</td>
                        <td className="px-2 py-3">
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-mono ${CATEGORY_BG[l.category] || CATEGORY_BG.Autre}`}>{l.category}</span>
                        </td>
                        <td className="px-2 py-3 text-right font-mono text-xs whitespace-nowrap">
                          {fmt(l.amount)} <span className="text-[9px] text-zinc-500">{l.currency}</span>
                          {(l.currency || "").toUpperCase() === "MAD" && (l.vat_amount != null || l.amount_ht != null) && (
                            <div
                              className="text-[9px] text-zinc-500 mt-0.5"
                              data-testid={`expenses-row-vat-${l.expense_line_id}`}
                              title={`HT ${l.amount_ht != null ? Number(l.amount_ht).toFixed(2) : "—"} · TVA ${l.vat_amount != null ? Number(l.vat_amount).toFixed(2) : "—"}${l.vat_rate != null ? ` (${Number(l.vat_rate).toFixed(0)}%)` : ""}`}
                            >
                              HT {l.amount_ht != null ? fmt(l.amount_ht) : "—"} · TVA {l.vat_amount != null ? fmt(l.vat_amount) : "—"}
                              {l.vat_rate != null && <span className="ml-0.5">({Number(l.vat_rate).toFixed(0)}%)</span>}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-3 text-right font-mono text-sm font-semibold whitespace-nowrap">{fmt(l.amount_mad)}</td>
                        <td className="px-2 py-3 text-xs" data-testid={`expenses-row-billable-${l.expense_line_id}`}>
                          {l.billable_to_client ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-900 font-mono text-[10px]">Oui</span>
                          ) : (
                            <span className="text-zinc-400 font-mono text-[10px]">Non</span>
                          )}
                        </td>
                        <td className="px-2 py-3 text-xs text-zinc-700 max-w-[140px] truncate" title={l.billable_to_client ? (l.client_name || l.mission_title || "") : ""} data-testid={`expenses-row-client-${l.expense_line_id}`}>
                          {l.billable_to_client ? (l.client_name || l.mission_title || "—") : "—"}
                        </td>
                        <td className="px-2 py-3 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            {l.receipt_drive_web_view_link && (
                              <a href={l.receipt_drive_web_view_link} target="_blank" rel="noreferrer" title="Voir le ticket"
                                className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded" data-testid={`expenses-row-view-${l.expense_line_id}`}>
                                <FileText size={13} />
                              </a>
                            )}
                            {/* Iter-292 — receipt staged but not yet on Drive (auto-retry every 15 min). */}
                            {!l.receipt_drive_web_view_link && l.staged_file_id && !l.receipt_drive_file_id && (
                              <a href={`/api/expenses/ocr/staged/${l.staged_file_id}/preview`} target="_blank" rel="noreferrer"
                                title={`Ticket en attente d'upload Drive — ${l.receipt_drive_error || "nouvel essai automatique toutes les 15 min"}`}
                                className="p-1.5 text-amber-600 hover:bg-amber-50 rounded inline-flex items-center" data-testid={`expenses-row-pending-${l.expense_line_id}`}>
                                <FileText size={13} />
                                <AlertTriangle size={9} className="-ml-1 -mt-2" />
                              </a>
                            )}
                            {switchCap?.allowed && (
                              <SwitchLineButton direction="to-cabinet" lineId={l.expense_line_id}
                                disabledReason={!rowEditable
                                  ? `Bascule indisponible : NDF ${n.status === "PAID" ? "payée" : n.status === "SUBMITTED" ? "en cours de validation" : "validée"}`
                                  : (switchCap.cabinet_block || (l.billable_to_client ? "Ligne refacturable au client — retirez la refacturation avant de la basculer" : null))}
                                onDone={() => loadMonth(viewYear, viewMonth)} />
                            )}
                            {rowEditable && (
                              <>
                                {(l.is_duplicate || l.overridden_duplicate) && (
                                  <IconAction icon={l.overridden_duplicate ? X : Check} tone={l.overridden_duplicate ? "amberSoft" : "emeraldSoft"} testid={`expenses-row-toggle-notdup-${l.expense_line_id}`} label={l.overridden_duplicate ? "Annuler — re-marquer comme doublon potentiel" : "Confirmer que cette ligne n'est PAS un doublon"} onClick={() => toggleNotDuplicate(l)} />
                                )}
                                <IconAction icon={Pencil} testid={`expenses-row-edit-${l.expense_line_id}`} label="Modifier la ligne" onClick={() => setEditLine({ ...l, _isNew: false })} />
                                <IconAction icon={Trash2} tone="danger" testid={`expenses-row-delete-${l.expense_line_id}`} label="Supprimer la ligne" onClick={() => deleteLine(l.expense_line_id)} />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {ls.length > 0 && (
                    <tfoot>
                      <tr className="bg-zinc-50 border-t border-zinc-200 font-mono text-sm">
                        <td colSpan={5} className="px-2 py-3 text-right text-zinc-700 font-semibold whitespace-nowrap">Total MAD TTC :</td>
                        <td className="px-2 py-3 text-right font-bold whitespace-nowrap" data-testid={`expenses-total-${n.expense_note_id}`}>{fmt(n.total_mad)} MAD</td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              {/* Iter-194k — REVOKED note banner: shows the validator's motif
                  so the collaborator knows why the NDF was re-opened, and the
                  same submit-gating logic below picks it up automatically. */}
              {isRevoked && (
                <div
                  data-testid={`expenses-revoke-banner-${n.expense_note_id}`}
                  className="px-4 py-3 border-t border-zinc-100 flex items-start gap-2 text-sm text-amber-900 bg-amber-50"
                >
                  <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Validation annulée — NDF ré-ouverte en brouillon</div>
                    <div className="text-amber-800 mt-0.5 break-words">
                      Motif : {n.revoke_reason || "(non précisé)"}
                    </div>
                    <div className="text-[11px] text-amber-700 mt-1">
                      Corrigez les lignes nécessaires puis cliquez « Soumettre la NDF » pour la re-soumettre.
                    </div>
                  </div>
                </div>
              )}
              {/* Iter-193m + 193t + 195y — Duplicate banner + submit gating, per active NDF only */}
              {n.status === "DRAFT" && ls.length > 0 && (() => {
                const overrideCount = ls.filter((l) => l.overridden_duplicate).length;
                return (
                <div className="px-4 py-3 border-t border-zinc-100 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                  <div className="flex flex-col gap-2 text-sm">
                    {dupCount > 0 && (
                      <div
                        data-testid={`expenses-duplicate-banner-${n.expense_note_id}`}
                        className="flex items-start gap-2 text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                      >
                        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                        <span>
                          <b>{dupCount}</b> doublon(s) détecté(s). Modifiez, supprimez, ou cliquez sur <Check size={11} className="inline -mt-0.5 text-emerald-700" /> sur la ligne pour confirmer « pas un doublon » (le valideur en sera informé).
                        </span>
                      </div>
                    )}
                    {overrideCount > 0 && (
                      <div
                        data-testid={`expenses-override-banner-${n.expense_note_id}`}
                        className="flex items-start gap-2 text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                      >
                        <Check size={16} className="mt-0.5 shrink-0 text-amber-700" />
                        <span>
                          <b>{overrideCount}</b> ligne(s) marquée(s) « pas un doublon » — sera(ont) signalée(s) au valideur pour vérification finale.
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => submitNote(n)}
                    disabled={dupCount > 0}
                    data-testid={`expenses-submit-btn-${n.expense_note_id}`}
                    className="inline-flex items-center gap-1.5 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    title={dupCount > 0 ? "Résolvez les doublons pour pouvoir soumettre (modifiez, supprimez, ou confirmez « pas un doublon »)" : undefined}
                  >
                    <Send size={14} /> Soumettre la NDF
                  </button>
                </div>
                );
              })()}
              {/* Iter-194c — REJECTED notes: show the rejection reason + a
                  "Re-soumettre" button that re-opens the note in DRAFT so the
                  collaborator can correct and submit again. */}
              {n.status === "REJECTED" && (
                <div className="px-4 py-3 border-t border-zinc-100 flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div
                    data-testid={`expenses-rejection-banner-${n.expense_note_id}`}
                    className="flex-1 flex items-start gap-2 text-sm text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                  >
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold">NDF rejetée</div>
                      <div className="text-red-800 mt-0.5 break-words">
                        Motif : {n.rejection_reason || "(non précisé)"}
                      </div>
                      <div className="text-[11px] text-red-700 mt-1">
                        Corrigez les lignes concernées (icônes crayon / corbeille) puis cliquez « Re-soumettre ».
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => resubmitNote(n)}
                    data-testid={`expenses-resubmit-btn-${n.expense_note_id}`}
                    className="inline-flex items-center gap-1.5 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 font-medium whitespace-nowrap"
                    title="Re-soumettre la NDF corrigée pour validation"
                  >
                    <RefreshCw size={14} /> Re-soumettre
                  </button>
                </div>
              )}
            </section>
          );
        })}
      </div>

      <EditLineDialog
        categories={feature.categories}
        editLine={editLine}
        onClose={() => setEditLine(null)}
        onSave={saveLine}
      />
      <NotDuplicateOverrideDialog
        target={overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onSubmit={submitOverride}
      />
    </div>
  );
}

// Iter-195z — Small modal asking the collaborator to justify why a flagged
// line is NOT actually a duplicate. Reason is optional (we accept empty) but
// strongly encouraged so the validator has context during the final review.
function NotDuplicateOverrideDialog({ target, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setReason(""); }, [target]);
  if (!target) return null;
  const line = target.line;
  const handleSubmit = async () => {
    setSaving(true);
    try { await onSubmit(reason); }
    finally { setSaving(false); }
  };
  const remaining = 280 - reason.length;
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="expenses-override-dialog">
        <DialogHeader>
          <DialogTitle>Confirmer « pas un doublon »</DialogTitle>
          <DialogDescription className="text-xs">
            Cette ligne sera marquée comme distincte. Ajoutez un motif si vous le souhaitez — il sera visible par le valideur lors de la validation finale.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-3 py-2 font-mono">
            {line.invoice_date} · <b>{line.vendor}</b> · {Number(line.amount).toFixed(2)} {line.currency}
            {line.invoice_number ? ` · n° ${line.invoice_number}` : ""}
          </div>
          <div>
            <Label htmlFor="override-reason-input" className="text-xs">Motif (optionnel)</Label>
            <textarea
              id="override-reason-input"
              data-testid="expenses-override-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 280))}
              rows={3}
              placeholder="Ex. : deux repas distincts avec collègues, ou 2 trajets le même jour…"
              className="mt-1 w-full text-sm border border-zinc-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 resize-none"
              autoFocus
            />
            <div className={`text-[10px] font-mono mt-1 text-right ${remaining < 30 ? "text-amber-600" : "text-zinc-400"}`}>
              {remaining} caractères restants
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="expenses-override-cancel"
            className="px-3 h-9 text-sm border border-zinc-200 rounded-md hover:bg-zinc-50"
            disabled={saving}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            data-testid="expenses-override-submit"
            className="px-4 h-9 bg-emerald-700 text-white text-sm rounded-md hover:bg-emerald-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={saving}
          >
            {saving ? "…" : "Confirmer"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditLineDialog({ editLine, categories, onClose, onSave }) {
  const [form, setForm] = useState(editLine || null);
  const [saving, setSaving] = useState(false);
  const [missions, setMissions] = useState([]);
  const [missionsLoading, setMissionsLoading] = useState(false);
  useEffect(() => { setForm(editLine); }, [editLine]);

  // Iter-193i — fetch the user's missions for the line's month, used by the
  // "Refacturable au client" dropdown. Re-fires when the date changes.
  useEffect(() => {
    if (!form || !form.invoice_date) { setMissions([]); return; }
    const ym = form.invoice_date.slice(0, 7); // YYYY-MM
    const [y, m] = ym.split("-");
    if (!y || !m) return;
    setMissionsLoading(true);
    api.get(`/expenses/billable-missions?year=${parseInt(y, 10)}&month=${parseInt(m, 10)}`)
      .then((r) => setMissions(r.data || []))
      .catch(() => setMissions([]))
      .finally(() => setMissionsLoading(false));
  }, [form?.invoice_date]);

  if (!editLine || !form) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Iter-193j — max selectable date: today + 1 day (24h tolerance).
  const maxDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  })();

  // Iter-193r — preview URL must be same-origin so the httponly session cookie
  // is sent by the browser when <img src=…> fetches the staged file. Using
  // process.env.REACT_APP_BACKEND_URL froze the URL at build time and, in
  // production on baim.ma, pointed to a different origin → cookies dropped →
  // 401 → broken preview. A relative path resolves to the current origin.
  const previewUrl = form.staged_file_id
    ? `/api/expenses/ocr/staged/${form.staged_file_id}/preview`
    : (form.receipt_drive_web_view_link || null);
  const isPdfPreview = previewUrl && (
    (form.staged_mime || "").toLowerCase().includes("pdf") ||
    (previewUrl || "").toLowerCase().endsWith(".pdf")
  );

  const submit = async () => {
    if (!form.invoice_date || !form.vendor || !form.amount || !form.currency) {
      toast.error("Champs requis : Date, Fournisseur, Montant, Devise");
      return;
    }
    // Iter-193j — block future dates client-side (backend enforces +24h tolerance too).
    const todayPlus1 = new Date(); todayPlus1.setDate(todayPlus1.getDate() + 1);
    if (new Date(form.invoice_date) > todayPlus1) {
      toast.error("La date du ticket ne peut pas être dans le futur (au-delà de +24h).");
      return;
    }
    if (form.billable_to_client && !form.mission_id) {
      toast.error("Sélectionnez une mission pour la refacturation client");
      return;
    }
    setSaving(true);
    const isMAD = (form.currency || "").toUpperCase() === "MAD";
    const num = (v) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      invoice_date: form.invoice_date,
      vendor: form.vendor.trim(),
      invoice_number: form.invoice_number || null,
      category: form.category,
      amount: parseFloat(form.amount),
      currency: form.currency.toUpperCase(),
      description: form.description || null,
      billable_to_client: !!form.billable_to_client,
      mission_id: form.billable_to_client ? (form.mission_id || null) : null,
      // Iter-207bb — VAT breakdown for MAD invoices only.
      amount_ht: isMAD ? num(form.amount_ht) : null,
      vat_amount: isMAD ? num(form.vat_amount) : null,
      vat_rate: isMAD ? num(form.vat_rate) : null,
      amount_ttc: isMAD ? num(form.amount_ttc) : null,
    };
    if (form._isNew) {
      payload.staged_file_id = form.staged_file_id || null;
      payload.receipt_drive_file_id = form.receipt_drive_file_id || null;
      payload.receipt_drive_web_view_link = form.receipt_drive_web_view_link || null;
    }
    await onSave(payload, form._isNew ? null : form.expense_line_id, form);
    setSaving(false);
  };

  return (
    <Dialog open={!!editLine} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="max-w-lg" data-testid="expenses-edit-dialog">
        <DialogHeader>
          <DialogTitle>
            {form._isNew ? "Nouvelle dépense" : "Modifier la dépense"}
            {form._queueTotal > 1 && (
              <span
                className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-wider"
                data-testid="expenses-edit-queue-badge"
              >
                Ticket {form._queueIndex || 1} / {form._queueTotal}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {form._ocrError
              ? "L'OCR n'a pas pu lire ce ticket. Remplissez les champs à la main — le document reste joint."
              : (form.confidence != null
                ? `Champs pré-remplis automatiquement (confiance ${Math.round(form.confidence * 100)}%). Vérifiez avant d'enregistrer.`
                : "Saisissez les informations de la dépense.")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Iter-193r — surface the OCR failure inline so the user understands
              why the fields aren't pre-filled. */}
          {form._ocrError && (
            <div
              data-testid="expenses-edit-ocr-error"
              className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs"
            >
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">OCR indisponible</div>
                <div className="text-amber-800 mt-0.5 break-words">{form._ocrError}</div>
              </div>
            </div>
          )}
          {/* Iter-193j — preview of the scanned receipt next to the extracted fields */}
          {previewUrl && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-2 relative" data-testid="expenses-edit-preview-wrapper">
              {isPdfPreview ? (
                <ScannedPdfPreview url={previewUrl} testid="expenses-edit-preview-pdf" />
              ) : (
                <img
                  src={previewUrl}
                  alt="Aperçu du ticket scanné"
                  className="w-full max-h-72 object-contain rounded"
                  data-testid="expenses-edit-preview-image"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
              {/* Iter-193p — surface the auto-crop status */}
              {form.auto_cropped && (
                <span
                  data-testid="expenses-edit-autocrop-badge"
                  className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600/90 text-white text-[10px] font-mono uppercase tracking-wider"
                  title="Le ticket a été recadré automatiquement avant l'analyse."
                >
                  <Check size={10} /> Recadré auto
                </span>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date *"><input type="date" max={maxDate} value={form.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} data-testid="expenses-edit-date" /></Field>
            <Field label="Catégorie">
              <select value={form.category || "Autre"} onChange={(e) => set("category", e.target.value)} data-testid="expenses-edit-category">
                {(categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Fournisseur *"><input type="text" value={form.vendor || ""} onChange={(e) => set("vendor", e.target.value)} data-testid="expenses-edit-vendor" /></Field>
          <Field label="N° Facture / Commande"><input type="text" value={form.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} data-testid="expenses-edit-invoice-number" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Montant TTC *"><input type="number" step="0.01" min="0" value={form.amount || ""} onChange={(e) => set("amount", e.target.value)} data-testid="expenses-edit-amount" /></Field>
            <Field label="Devise *">
              <select value={form.currency || "MAD"} onChange={(e) => set("currency", e.target.value)} data-testid="expenses-edit-currency">
                {["MAD", "EUR", "USD", "XOF", "GBP", "AED", "TND"].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          {/* Iter-207bb — VAT breakdown panel, only for MAD invoices. */}
          {(form.currency || "MAD").toUpperCase() === "MAD" && (
            <div
              className="border border-zinc-200 rounded-md p-3 space-y-2 bg-zinc-50/50"
              data-testid="expenses-edit-vat-block"
            >
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                Détail TVA (MAD) — extrait automatiquement par l'OCR
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Montant HT">
                  <input
                    type="number" step="0.01" min="0"
                    value={form.amount_ht ?? ""}
                    onChange={(e) => set("amount_ht", e.target.value)}
                    data-testid="expenses-edit-amount-ht"
                  />
                </Field>
                <Field label="TVA (%)">
                  <input
                    type="number" step="0.1" min="0" max="100"
                    value={form.vat_rate ?? ""}
                    onChange={(e) => set("vat_rate", e.target.value)}
                    data-testid="expenses-edit-vat-rate"
                  />
                </Field>
                <Field label="Montant TVA">
                  <input
                    type="number" step="0.01" min="0"
                    value={form.vat_amount ?? ""}
                    onChange={(e) => set("vat_amount", e.target.value)}
                    data-testid="expenses-edit-vat-amount"
                  />
                </Field>
                <Field label="Montant TTC (récap.)">
                  <input
                    type="number" step="0.01" min="0"
                    value={form.amount_ttc ?? ""}
                    onChange={(e) => set("amount_ttc", e.target.value)}
                    data-testid="expenses-edit-amount-ttc"
                  />
                </Field>
              </div>
              <p className="text-[10px] text-zinc-500 leading-relaxed">
                Laissez vide si le ticket ne comporte pas de ligne TVA explicite.
                Le « Montant TTC » ci-dessus reste la valeur officielle utilisée pour la conversion en MAD.
              </p>
            </div>
          )}
          <Field label="Description"><textarea rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="expenses-edit-description" /></Field>
          {/* Iter-193i — refacturable au client */}
          <div className="border-t border-zinc-200 pt-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-zinc-700 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.billable_to_client}
                onChange={(e) => set("billable_to_client", e.target.checked)}
                data-testid="expenses-edit-billable-checkbox"
                className="h-4 w-4 accent-zinc-900"
              />
              Refacturable au client
            </label>
            {form.billable_to_client && (
              <>
                <Field label="Mission à refacturer *">
                  <select
                    value={form.mission_id || ""}
                    onChange={(e) => set("mission_id", e.target.value)}
                    data-testid="expenses-edit-mission-select"
                    disabled={missionsLoading}
                  >
                    <option value="">{missionsLoading ? "Chargement…" : "— Sélectionner —"}</option>
                    {missions.map((m) => (
                      <option key={m.mission_id} value={m.mission_id}>
                        {m.title}{m.client_name ? ` — ${m.client_name}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                {!missionsLoading && missions.length === 0 && (
                  <div
                    className="text-xs text-amber-700"
                    data-testid="expenses-edit-no-missions-warning"
                  >
                    Aucune mission active pour ce mois — décochez ou changez la date.
                  </div>
                )}
              </>
            )}
          </div>
          {form.receipt_drive_web_view_link && (
            <a href={form.receipt_drive_web_view_link} target="_blank" rel="noreferrer"
              className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">
              <FileText size={11} /> Voir le ticket scanné
            </a>
          )}
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} disabled={saving} data-testid="expenses-edit-cancel"
            className="px-3 h-9 text-sm border border-zinc-200 rounded hover:bg-zinc-50">Annuler</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="expenses-edit-save"
            className="px-3 h-9 text-sm bg-zinc-950 text-white rounded hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center gap-1.5">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Enregistrer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  // Iter-193i — fail fast when a consumer accidentally passes 0 or 2+ children.
  // The previous implementation silently called React.cloneElement on an array
  // (e.g. [<select/>, falseOrDiv]) which threw the cryptic "Element type is
  // invalid: ... got undefined" red overlay, taking down the dialog.
  const onlyChild = React.Children.only(children);
  return (
    <label className="block">
      <Label className="text-xs text-zinc-600 mb-1 block">{label}</Label>
      {React.cloneElement(onlyChild, {
        className: "w-full px-2.5 py-1.5 border border-zinc-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950",
      })}
    </label>
  );
}
